import React, { useEffect, useRef, useState } from 'react';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import useSpeechstream from '@/components/shared/TextToSpeechButton/useSpeechStream';
import { currentSelectionRange, restoreSelectionRange } from '@/utils/selection';
import LanguageSelectMenu from './LanguageSelectMenu';
import ButtonWithAdjacentDropdown from '../Button/ButtonWithAdjacentDropdown';

interface TranslateButtonProps {
  iconFill?: string;
  isInDraggableToolbar?: boolean;
  locale: Locale;
  userLanguage: string;
}

const TranslateButton = (
  { iconFill, isInDraggableToolbar, locale, userLanguage }: TranslateButtonProps,
) => {
  const {
    loaded,
    setTranslationDestination,
    setTranslationSource,
    translateSelection,
  } = useSpeechstream({ idToRead: '', locale });

  const savedSelectionRef = useRef(null);
  const preserveSelectionRef = useRef(false);
  const [selectedLanguage, setSelectedLanguage] = useState(userLanguage);
  const [selectedText, setSelectedText] = useState('');

  const isEnabled = selectedText?.length > 0;
  const iconColor = isEnabled ? (iconFill || '#522e91') : '#ccc';

  const saveSelection = () => {
    const range = currentSelectionRange();
    if (range) savedSelectionRef.current = range;
  };

  // AIDEV-NOTE: On touch devices, tapping the Translate button collapses the page selection
  // before the click fires. Stash the range while text is selected and swallow the single
  // button-triggered collapse so the button stays enabled and the range survives for translate().
  const onSelectionChanged = () => {
    const selection = window.getSelection();
    const text = selection?.toString() ?? '';

    if (text.length === 0 && preserveSelectionRef.current) {
      preserveSelectionRef.current = false;
      return;
    }

    if (text.length > 0) saveSelection();
    setSelectedText(text);
  };

  useEffect(() => {
    document.addEventListener('selectionchange', onSelectionChanged);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChanged);
    };
  }, []);

  if (!loaded) return null;

  const tooltipLabel = () => {
    if (selectedText.length > 0) {
      if (selectedLanguage) {
        return I18n.t('translate_selected_text_into', { language: selectedLanguage });
      }
      return I18n.t('choose_a_language_to_translate_the_selected_text');
    }

    return I18n.t('select_text_to_translate_into_another_language');
  };

  const restoreSelection = () => restoreSelectionRange(savedSelectionRef.current);

  const translateSelectedText = language => (
    setTranslationSource(locale === 'en' ? 'English' : 'Spanish')?.then(() => {
      setTranslationDestination(language).then((res) => {
        setSelectedLanguage(res.englishDescription);
        restoreSelection();
        translateSelection();
      });
    })
  );

  const handleButtonClick = async () => {
    // Desktop taps don't collapse the selection, so onSelectionChanged never runs its reset;
    // clear the flag here so a later genuine deselect still disables the button.
    preserveSelectionRef.current = false;
    saveSelection();

    if (!isEnabled) return;

    await translateSelectedText(selectedLanguage);
  };

  return (
    <div className="tw-flex">
      <ButtonWithAdjacentDropdown
        ariaLabel={tooltipLabel()}
        caretColor={iconColor}
        disabled={!isEnabled}
        dropDownAriaLabel={I18n.t('select_language_to_translate')}
        isInDraggableToolbar={isInDraggableToolbar}
        onAdjacentButtonClick={saveSelection}
        onButtonClick={handleButtonClick}
        onPointerDown={() => { preserveSelectionRef.current = true; }}
      >
        <ButtonWithAdjacentDropdown.ButtonContent>
          <Icon fill={iconColor} icon="Translate" />
        </ButtonWithAdjacentDropdown.ButtonContent>
        <ButtonWithAdjacentDropdown.DropdownContent
          className="tw-w-auto"
        >
          <ButtonWithAdjacentDropdown.Close asChild>
            <LanguageSelectMenu
              locale={locale}
              restoreSelection={restoreSelection}
              selectedLanguage={selectedLanguage}
              setLanguage={language => translateSelectedText(language)}
            />
          </ButtonWithAdjacentDropdown.Close>
        </ButtonWithAdjacentDropdown.DropdownContent>
      </ButtonWithAdjacentDropdown>
    </div>
  );
};

export default TranslateButton;
